/**
 * Channel caption rules — the deterministic quality pass for the GENERIC
 * caption path (translateGeneric only; the Arabic crown jewel never runs it).
 *
 * Exists because the operator's studio tweaks must become STANDING RULES,
 * not per-clip patches: he fixed "? at the start of a line", "no gold
 * anywhere" and "3 wrapped lines" on one video, and the next video
 * reproduced all three. Cue text can't carry between videos — the rules can.
 *
 *   1. migrateLeadingPunct — a cue/line must never START with closing
 *      punctuation (? ! . , ; : » …). The stray char belongs to the previous
 *      cue's sentence end (French: "…dès le départ ?", not "? Et je dis").
 *   2. enforceMaxLines — captions are capped at N (default 2) rendered
 *      lines; an over-budget cue SPLITS into sequential cues at a clause
 *      boundary, dividing its time window (the studio's "max 2 lines →
 *      second caption" behaviour, applied automatically at pipeline time).
 *   3. ensureAccentSpans — every cue gets ONE accent (<em>) keyword when the
 *      model left it unstyled (uniform channels color ONLY via spans, so a
 *      span-less SRT renders all-white). Tiered pick, stopword-guarded —
 *      same philosophy as the Arabic path's ensureEmOnOneLineCues: a bad
 *      accent is worse than no accent.
 *
 * Kill switch: QL_CAPTION_RULES=off (checked by the caller).
 * Pure module — no I/O, unit-testable.
 */
import { splitCueIntoParts } from "./clipEditState.ts";
import { hasEmTag, stripEmTags, type SrtCue } from "./srt.ts";

export interface CaptionRulesOpts {
  /** Rendered-line cap; cues estimated above it split into sequential cues.
   *  0/undefined disables the pass. */
  maxLines?: number;
  /** Per-line char budget (language-tuned; e.g. French oneLine 22-ish). */
  charsPerLine: number;
  /** Ensure one accent span per unstyled cue (uniform channels — punch
   *  channels get their deterministic second-half gold elsewhere). */
  ensureAccent?: boolean;
  /** How many accent (<em>) keywords per cue when ensureAccent is on.
   *  Absent → 1 (founding). See ChannelConfig.cueShape.accentWords. */
  accentWords?: number;
  /** BCP-ish language code for the stopword list ("fr", "en", …). Unknown
   *  codes use the combined latin list. */
  langCode?: string;
  /** Measured fit (spec 2026-09-17): when present, REPLACES enforceMaxLines
   *  — cues are split by real glyph widths against the channel's design
   *  (src/lib/measuredFit.ts) instead of the language's character budget.
   *  Same slot in the order: punctuation → lines → accents. */
  fitter?: (cues: SrtCue[]) => SrtCue[];
}

/** Cap on how many cues one over-budget cue may split into. */
const MAX_SPLIT_PARTS = 4;

// ---------------------------------------------------------------------------
// 1. Leading punctuation migration
// ---------------------------------------------------------------------------

/** Closing/terminal marks that must never begin a line. « is OPENING — a
 *  legitimate line start — and is deliberately absent. */
const CLOSING_PUNCT = new Set(["?", "!", ".", ",", ";", ":", "»", "…", "%"]);
/** French typography wants a space before these when re-attached. */
const SPACE_BEFORE = new Set(["?", "!", ";", ":", "»"]);

/** Pull leading closing-punctuation off a string; returns [punctRun, rest]. */
function takeLeadingPunct(s: string): [string, string] {
  let i = 0;
  while (i < s.length && (CLOSING_PUNCT.has(s[i]!) || s[i] === " ")) i++;
  const run = s.slice(0, i).trim();
  return run.length > 0 ? [run, s.slice(i).trimStart()] : ["", s];
}

function appendPunct(base: string, punct: string): string {
  const sep = SPACE_BEFORE.has(punct[0]!) && !base.endsWith(" ") ? " " : "";
  return base + sep + punct;
}

/** Cue- and line-leading closing punctuation migrates to the end of the
 *  previous cue / previous line. Cue 0's leading punct (nothing before it)
 *  is left untouched. */
export function migrateLeadingPunct(cues: SrtCue[]): SrtCue[] {
  const out = cues.map((c) => ({ ...c }));
  for (let i = 0; i < out.length; i++) {
    // Cue-leading → previous cue's last line.
    if (i > 0) {
      const [run, rest] = takeLeadingPunct(out[i]!.text);
      if (run && rest) {
        out[i]!.text = rest;
        const prevLines = out[i - 1]!.text.split("\n");
        prevLines[prevLines.length - 1] = appendPunct(prevLines[prevLines.length - 1]!, run);
        out[i - 1]!.text = prevLines.join("\n");
      }
    }
    // Line-leading (inside a multi-line cue) → previous line of the same cue.
    const lines = out[i]!.text.split("\n");
    for (let j = 1; j < lines.length; j++) {
      const [run, rest] = takeLeadingPunct(lines[j]!);
      if (run && rest) {
        lines[j - 1] = appendPunct(lines[j - 1]!, run);
        lines[j] = rest;
      }
    }
    out[i]!.text = lines.filter((l) => l.trim().length > 0).join("\n");
  }
  return out.filter((c) => c.text.trim().length > 0);
}

// ---------------------------------------------------------------------------
// 2. Max-lines enforcement (split into sequential cues)
// ---------------------------------------------------------------------------

const visibleLen = (text: string): number =>
  stripEmTags(text).replace(/\n/g, " ").trim().length;

/** Words a split should sit BEFORE (clause openers), lowercased. */
const SPLIT_BEFORE = new Set([
  // fr
  "et", "ou", "mais", "donc", "car", "parce", "que", "qui", "quand", "pour", "dans", "alors",
  // en
  "and", "or", "but", "because", "that", "which", "when", "so", "then",
]);

/** Best split index in `words` (split happens BEFORE the returned index):
 *  prefer after punctuation, then before a clause opener, then nearest the
 *  middle — always at a word boundary. `minWords` (default 1 — the founding
 *  behaviour) keeps at least that many words on EACH side, so a split never
 *  strands a one-word stub ("…changer," / "car"); when no boundary can
 *  satisfy it the guard is dropped rather than refusing to split. */
export function bestSplitIdx(words: string[], minWords = 1): number {
  const mid = words.length / 2;
  let best = Math.round(mid);
  let bestScore = -Infinity;
  const lo = Math.max(1, minWords);
  const hi = words.length - Math.max(1, minWords);
  if (lo > hi && minWords > 1) return bestSplitIdx(words, 1);
  for (let i = lo; i <= hi; i++) {
    const dist = Math.abs(i - mid) / words.length;
    let score = -dist;
    if (/[,;:.!?»…]$/.test(words[i - 1]!)) score += 0.6;
    if (SPLIT_BEFORE.has(words[i]!.toLowerCase().replace(/[^\p{L}']/gu, ""))) score += 0.3;
    if (score > bestScore) {
      bestScore = score;
      best = i;
    }
  }
  return best;
}

/** Split one over-budget text into ≤maxParts pieces each fitting the budget
 *  (best effort — a piece that still can't fit at maxParts stays long). */
function splitTextToBudget(text: string, budget: number, maxParts: number): string[] {
  const flat = text.replace(/\n/g, " ").replace(/\s+/g, " ").trim();
  if (flat.length <= budget || maxParts <= 1) return [flat];
  const words = flat.split(" ");
  if (words.length < 2) return [flat];
  const at = bestSplitIdx(words);
  const left = words.slice(0, at).join(" ");
  const right = words.slice(at).join(" ");
  // Give each side a fair share of the remaining part allowance.
  const leftParts = splitTextToBudget(left, budget, Math.max(1, Math.floor(maxParts / 2)));
  const rightParts = splitTextToBudget(right, budget, Math.max(1, maxParts - leftParts.length));
  return [...leftParts, ...rightParts];
}

/** Cues whose estimated wrap exceeds `maxLines` split into sequential cues
 *  that tile the original time window (weighted by part length). Existing
 *  accent tags on a split cue are stripped (ensureAccentSpans re-accents
 *  each part afterwards — a tag pair must never be cut in half). */
export function enforceMaxLines(cues: SrtCue[], maxLines: number, charsPerLine: number): SrtCue[] {
  const budget = maxLines * charsPerLine;
  const out: SrtCue[] = [];
  for (const cue of cues) {
    if (visibleLen(cue.text) <= budget) {
      out.push({ ...cue });
      continue;
    }
    const clean = hasEmTag(cue.text) ? stripEmTags(cue.text) : cue.text;
    const parts = splitTextToBudget(clean, budget, MAX_SPLIT_PARTS);
    if (parts.length < 2) {
      out.push({ ...cue });
      continue;
    }
    const timed = splitCueIntoParts(
      { startMs: cue.startMs, endMs: cue.endMs, text: clean },
      parts,
    );
    for (const t of timed) {
      out.push({ index: 0, startMs: t.startMs, endMs: t.endMs, text: t.text });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// 3. Accent (em) guarantee
// ---------------------------------------------------------------------------

/** Never-accent words (lowercased). Pronouns, articles, copulas, discourse
 *  glue — an accent on these reads as a mistake. */
const STOPWORDS = new Set([
  // fr
  "le", "la", "les", "un", "une", "des", "du", "de", "d'un", "d'une", "et", "ou", "mais",
  "donc", "or", "ni", "car", "que", "qui", "quoi", "dont", "où", "je", "tu", "il", "elle",
  "on", "nous", "vous", "ils", "elles", "ce", "cette", "ces", "cet", "se", "sa", "son",
  "ses", "leur", "leurs", "mon", "ma", "mes", "ton", "ta", "tes", "notre", "votre", "nos",
  "vos", "ne", "pas", "plus", "moins", "très", "bien", "c'est", "n'est", "s'est", "est",
  "sont", "être", "était", "avoir", "fait", "faire", "comme", "pour", "dans", "sur",
  "avec", "sans", "sous", "chez", "par", "entre", "vers", "alors", "ainsi", "aussi",
  "encore", "déjà", "toujours", "jamais", "rien", "tout", "tous", "toute", "toutes",
  "quelque", "quelques", "même", "mêmes", "autre", "autres", "peu", "beaucoup", "trop",
  "assez", "quand", "si", "oui", "non", "ça", "cela", "celui", "celle", "ceux", "celles",
  "y", "en", "au", "aux", "qu'il", "qu'elle", "qu'on", "j'ai", "là", "ici", "puis",
  "parce", "vraiment", "juste", "voilà", "leur", "ouais", "peut", "peux", "veut", "veux",
  "dire", "dis", "dit", "va", "vais", "vont", "aller", "cette",
  // en
  "the", "a", "an", "and", "or", "but", "if", "then", "this", "that", "these", "those",
  "i", "you", "he", "she", "it", "we", "they", "is", "are", "was", "were", "be", "been",
  "being", "have", "has", "had", "do", "does", "did", "not", "no", "yes", "very",
  "really", "just", "so", "as", "at", "by", "for", "from", "in", "into", "of", "on",
  "to", "with", "about", "there", "here", "what", "which", "who", "when", "how", "why",
  "can", "could", "will", "would", "should", "your", "my", "our", "their", "his", "her",
  "its", "me", "him", "them", "us", "get", "got", "go", "going", "know", "like", "well",
  "yeah", "okay", "mean", "thing", "things",
]);

/** The word's accentable core (letters/digits/apostrophes, outer punct shed). */
function wordCore(token: string): string {
  return token.replace(/^[^\p{L}\p{N}]+/u, "").replace(/[^\p{L}\p{N}'’]+$/u, "");
}

/** Pick the accent keyword for a cue: longest non-stopword ≥5 chars, else
 *  ≥4; none → null (leave the cue cream — a bad accent is worse than none).
 *  `exclude` — accentable cores already tagged (case-sensitive) — is skipped,
 *  so a second/third pass (ensureAccentSpans with wordsPerCue > 1) doesn't
 *  re-pick the word it just wrapped in <em>. */
export function pickAccentWord(text: string, exclude?: Set<string>): string | null {
  const cores = stripEmTags(text)
    .split(/\s+/)
    .map(wordCore)
    .filter((c) => c.length > 0 && !STOPWORDS.has(c.toLowerCase()) && !exclude?.has(c));
  for (const minLen of [5, 4]) {
    const candidates = cores.filter((c) => c.length >= minLen);
    if (candidates.length > 0) {
      return candidates.reduce((a, b) => (b.length > a.length ? b : a));
    }
  }
  return null;
}

/** A complete `<em …>…</em>` run, tags included. */
const EM_RUN_RE = /<em\b[^>]*>[\s\S]*?<\/em>/g;

const isWordChar = (c: string | undefined): boolean => !!c && /[\p{L}\p{N}]/u.test(c);

/**
 * Index at which `word` may be wrapped in a NEW `<em>` span, or -1.
 *
 * Two guards, both needed when a later pick is a SUBSTRING of an earlier one
 * ("manque" inside "manquerai"): the match must lie OUTSIDE every existing
 * `<em>…</em>` run — a naive `indexOf` lands inside the span just written and
 * yields `<em><em>manque</em>rai</em>`, nested markup `splitEmphasis` can't
 * parse — and it must be a WHOLE word, so the accent never splits a longer
 * word that merely starts with the same letters.
 */
function accentableIndex(text: string, word: string): number {
  const spans: [number, number][] = [];
  EM_RUN_RE.lastIndex = 0;
  for (let m = EM_RUN_RE.exec(text); m; m = EM_RUN_RE.exec(text)) {
    spans.push([m.index, m.index + m[0].length]);
  }
  for (let i = text.indexOf(word); i >= 0; i = text.indexOf(word, i + 1)) {
    const end = i + word.length;
    if (spans.some(([from, to]) => i < to && end > from)) continue; // inside a span
    if (isWordChar(text[i - 1]) || isWordChar(text[end])) continue; // mid-word
    return i;
  }
  return -1;
}

/** Wrap the N best keywords in <em> for every cue that has no accent span yet
 *  (N = wordsPerCue, default 1 — the founding rule). */
export function ensureAccentSpans(cues: SrtCue[], wordsPerCue = 1): SrtCue[] {
  if (wordsPerCue <= 0) return cues;
  return cues.map((cue) => {
    if (hasEmTag(cue.text)) return cue;
    let text = cue.text;
    const tagged = new Set<string>();
    for (let n = 0; n < wordsPerCue; n++) {
      const word = pickAccentWord(text, tagged);
      if (!word) break;
      const idx = accentableIndex(text, word);
      // No standalone occurrence left (every one sits inside a span already
      // written): skip this candidate and let the next pass pick another word
      // rather than nesting tags.
      if (idx < 0) {
        tagged.add(word);
        continue;
      }
      text = text.slice(0, idx) + `<em>${word}</em>` + text.slice(idx + word.length);
      tagged.add(word);
    }
    return text === cue.text ? cue : { ...cue, text };
  });
}

// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------

export function applyCaptionRules(cues: SrtCue[], opts: CaptionRulesOpts): SrtCue[] {
  let out = migrateLeadingPunct(cues);
  if (opts.fitter) {
    out = opts.fitter(out);
  } else if (opts.maxLines && opts.maxLines > 0) {
    out = enforceMaxLines(out, opts.maxLines, opts.charsPerLine);
  }
  if (opts.ensureAccent) {
    out = ensureAccentSpans(out, opts.accentWords ?? 1);
  }
  // Renumber 1..N (splits change the count).
  return out.map((c, i) => ({ ...c, index: i + 1 }));
}
